import React, { useState } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { PASSWORD_CHECKS, passwordValid } from "../lib/countryCodes";
import { ArrowRight, ArrowLeft, Eye, EyeSlash, CheckCircle, Circle } from "@phosphor-icons/react";

function errText(detail) {
  if (!detail) return "Reset failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const token = params.get("token") || location.state?.token || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const match = pw && pw === confirm;
  const canSubmit = token && passwordValid(pw) && match;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e) {
      setErr(errText(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5 py-10 grain">
        <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 text-center animate-fade-up" data-testid="reset-done">
          <CheckCircle size={64} weight="fill" className="text-jam-green mx-auto" />
          <h1 className="text-cream text-4xl font-black tracking-tighter mt-4 leading-none">Password<br />changed</h1>
          <p className="text-cream/70 mt-3 text-sm">Your password has been successfully changed.</p>
          <button data-testid="reset-return-login" onClick={() => nav("/login")} className="mt-7 w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2">
            Return to login <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5 py-10 grain">
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 animate-fade-up">
        <Link to="/login" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
          <ArrowLeft size={14} /> Back to login
        </Link>
        <p className="text-papaya text-xs tracking-[0.3em] uppercase font-bold">Reset</p>
        <h1 className="text-cream text-4xl font-black tracking-tighter mt-1 leading-none">Create new<br />password.</h1>

        {!token && (
          <p className="mt-4 text-red-400 text-sm" data-testid="reset-no-token">This reset link is missing its token. Please use the link from your email or restart recovery.</p>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <PwField label="New password" value={pw} onChange={setPw} show={show} toggle={() => setShow((s) => !s)} testid="reset-password" />
          <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={showConfirm} toggle={() => setShowConfirm((s) => !s)} testid="reset-confirm" />

          <ul className="space-y-1.5 pt-1" data-testid="reset-policy">
            {PASSWORD_CHECKS.map((c) => {
              const ok = c.test(pw);
              return (
                <li key={c.key} className={`flex items-center gap-2 text-xs ${ok ? "text-jam-green" : "text-cream/45"}`}>
                  {ok ? <CheckCircle size={14} weight="fill" /> : <Circle size={14} />} {c.label}
                </li>
              );
            })}
            <li className={`flex items-center gap-2 text-xs ${match ? "text-jam-green" : "text-cream/45"}`}>
              {match ? <CheckCircle size={14} weight="fill" /> : <Circle size={14} />} Passwords match
            </li>
          </ul>

          {err && <p data-testid="reset-error" className="text-red-400 text-sm">{err}</p>}
          <button data-testid="reset-submit" type="submit" disabled={busy || !canSubmit}
            className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? "Updating…" : <>Create new password <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, show, toggle, testid }) {
  return (
    <label className="block">
      <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">{label}</span>
      <div className="relative mt-2">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 pr-12 text-cream focus:outline-none focus:border-papaya"
        />
        <button type="button" onClick={toggle} data-testid={`${testid}-toggle`} className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/50 hover:text-papaya">
          {show ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </label>
  );
}
